// ─── Signalement du gérant ────────────────────────────────────────────────────
// Photo + lieu + délai → une tâche pour les présents à partir de demain
// (le matin d'un jour de livraison exclu), affichée jusqu'à ce qu'elle soit faite.
import { useState, useEffect } from 'react'
import { Camera, X } from 'lucide-react'
import api from '../lib/api'

const DELAIS = [
  { value: 'demain', label: 'Demain' },
  { value: '2j', label: 'Sous 2 jours' },
  { value: '3j', label: 'Sous 3 jours' },
  { value: 'semaine', label: 'Dans la semaine' },
]

function compressPhoto(file: File, max = 1280): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onload = ev => {
      const img = new Image()
      img.onerror = reject
      img.onload = () => {
        const scale = Math.min(max / img.width, max / img.height, 1)
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.7))
      }
      img.src = ev.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

export default function SignalementModal({ establishmentId, onClose, onDone }: {
  establishmentId: string; onClose: () => void; onDone: () => void
}) {
  const [lieux, setLieux] = useState<string[]>([])
  const [lieu, setLieu] = useState('')
  const [description, setDescription] = useState('')
  const [photo, setPhoto] = useState<string | null>(null)
  const [delai, setDelai] = useState('demain')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    api.get(`/tasks/lieux?establishmentId=${establishmentId}`).then(r => setLieux(r.data || [])).catch(() => {})
  }, [establishmentId])

  const envoyer = async () => {
    if (!lieu.trim()) { setError('Choisir le lieu'); return }
    setSaving(true); setError('')
    try {
      await api.post('/tasks/signalement', { establishmentId, lieu, description, photo, delai })
      onDone()
    } catch (e: any) { setError(e.response?.data?.error || 'Erreur') }
    finally { setSaving(false) }
  }

  const lieuDansListe = lieux.includes(lieu)
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold">Signaler un endroit à nettoyer</h2>
          <button onClick={onClose} aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Photo</label>
            <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-xl p-4 text-sm text-gray-500 cursor-pointer hover:border-pink-400">
              {photo ? <img src={photo} alt="signalement" className="max-h-48 rounded-lg" /> : <><Camera size={18} /> Prendre la photo</>}
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={async e => { const f = e.target.files?.[0]; if (f) setPhoto(await compressPhoto(f)) }} />
            </label>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Lieu</label>
            <select value={lieuDansListe ? lieu : ''} onChange={e => setLieu(e.target.value)}
              className="mt-1 block w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">— Choisir —</option>
              {lieux.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <input value={lieuDansListe ? '' : lieu} onChange={e => setLieu(e.target.value)}
              placeholder="…ou écrire un autre lieu" className="mt-2 w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Ce qu'il faut faire (facultatif)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" placeholder="Ex. traces sous l'étagère du bas" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Délai</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {DELAIS.map(d => (
                <button key={d.value} type="button" onClick={() => setDelai(d.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border ${delai === d.value ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-200'}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500">La tâche part demain aux personnes présentes (l'après-midi si c'est un jour de livraison) et reste affichée jusqu'à ce qu'elle soit faite.</p>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={envoyer} disabled={saving}
            className="flex items-center gap-2 bg-pink-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
            <Camera size={14} /> {saving ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  )
}
